import { useGtm } from '@/plugins/useGtm'
import type { BasketItem, Deposit, InstantWinDraw, Plan } from '@/types'
import { InstantWinType, ProductTypes } from '@/utils/helper-objects'

import { SettingsService } from './settings'

type SubscriptionPurchaseParams = {
  orderId: string
  orderRevenue: string
  planId: string
  externalId: string
  subscriberId: string
  awinSuppress: boolean
  planPeriod: string
  planDiscountCost: string
  customerType: string
}

type GamePurchaseParams = {
  instantWin: InstantWinDraw
  quantity: number
  purchaseId: number
  customerType: string
}

export class AnalyticsService {
  private static getInstantWinType(instantWinType: number) {
    return {
      [InstantWinType.Standard]: 'instant-win',
      [InstantWinType.MysteryBox]: 'mystery-box',
      [InstantWinType.InstantSpin]: 'instant-spin',
      [InstantWinType.InstantPack]: 'instant-pack',
    }[instantWinType]
  }

  private static getTotalAmount(product: InstantWinDraw, quantity: number) {
    return Number((product.ticket_cost * quantity).toFixed(2))
  }

  public static transformSubscriptionPurchaseParams(
    params: Record<string, any>
  ) {
    return {
      orderId: params.gl_order_id,
      orderRevenue: params.gl_order_revenue,
      planId: params.gl_plan_id,
      externalId: params.gl_external_id,
      subscriberId: params.gl_subscriber_id,
      awinSuppress: params.gl_awin_suppress === '1',
      planPeriod: params.gl_plan_period,
      planDiscountCost: params.gl_plan_discount_cost,
    }
  }

  public static async trackViewGame(params: InstantWinDraw) {
    const instantWinType = this.getInstantWinType(params.instant_win_type_id)

    const gtm = await useGtm()
    gtm?.trackEvent({
      event: 'view_item',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        items: [
          {
            item_id: params.code,
            item_name: params.title,
            price: params.ticket_cost,
            item_category: instantWinType,
          },
        ],
      },
    })
  }

  public static async trackViewSubscription(plans: Plan[]) {
    const gtm = await useGtm()
    gtm?.trackEvent({
      event: 'view_item',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        items: plans.map((plan) => ({
          item_id: plan.code,
          item_name: plan.name,
          price: plan.period === 'monthly' ? plan.cost : plan.discount_cost,
          item_category: ProductTypes.Subscription,
        })),
      },
    })
  }

  public static async trackBeginSubscriptionCheckout(params: BasketItem) {
    const gtm = await useGtm()
    const plan = params.product as Plan
    gtm?.trackEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        value: plan.cost,
        items: [
          {
            item_id: plan.code,
            item_name: plan.name,
            price: plan.period === 'monthly' ? plan.discount_cost : plan.cost,
            item_category: ProductTypes.Subscription,
            plan_id: plan.code,
            billing_period: plan.period,
            quantity: 1,
            index: 0,
          },
        ],
      },
    })
  }

  public static trackBeginCheckout(basketItems: BasketItem[]) {
    basketItems.forEach((item: BasketItem) => {
      switch (item.type) {
        case ProductTypes.InstantWinDraw:
          this.trackBeginGameCheckout(item)
          break
        case ProductTypes.Subscription:
          this.trackBeginSubscriptionCheckout(item)
          break
        case ProductTypes.Deposit:
          this.trackBeginDepositCheckout(item)
          break
        default:
          // No tracking for other product types yet
          break
      }
    })
  }

  public static async trackBeginDepositCheckout(params: BasketItem) {
    const gtm = await useGtm()
    const product = params.product as Deposit

    gtm?.trackEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        value: product.amount,
        items: [
          {
            item_id: product.sku,
            item_name: product.sku,
            price: product.amount,
            quantity: 1,
            index: 0,
          },
        ],
      },
    })
  }

  public static async trackBeginGameCheckout(params: BasketItem) {
    const gtm = await useGtm()
    const product = params.product as InstantWinDraw
    const instantWinType = this.getInstantWinType(product.instant_win_type_id)
    const totalAmount = this.getTotalAmount(product, params.quantity)

    gtm?.trackEvent({
      event: 'begin_checkout',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        value: totalAmount,
        items: [
          {
            item_id: product.code,
            item_name: product.title,
            price: product.ticket_cost,
            item_category: instantWinType,
            quantity: params.quantity,
            index: 0,
          },
        ],
      },
    })
  }

  public static async trackSubscriptionPurchase(
    params: SubscriptionPurchaseParams
  ) {
    const gtm = await useGtm()
    gtm?.trackEvent({
      event: 'purchase',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        transaction_id: params.orderId,
        value: params.orderRevenue,
        type: 'standard',
        customer_type: params.customerType,
        awin_suppress: params.awinSuppress,
        items: [
          {
            item_name: params.planId,
            item_id: params.planId,
            price: params.orderRevenue,
            item_category: ProductTypes.Subscription,
            plan_id: params.planId,
            billing_period: params.planPeriod,
            quantity: 1,
            index: 0,
          },
        ],
      },
    })
  }

  public static async trackGamePurchase(params: GamePurchaseParams) {
    const totalAmount = this.getTotalAmount(params.instantWin, params.quantity)
    const instantWinType = this.getInstantWinType(
      params.instantWin.instant_win_type_id
    )
    const gtm = await useGtm()

    gtm?.trackEvent({
      event: 'purchase',
      ecommerce: {
        currency: SettingsService.activeCurrency,
        transaction_id: params.purchaseId,
        value: totalAmount,
        type: instantWinType,
        customer_type: params.customerType,
        items: [
          {
            item_name: params.instantWin.title,
            item_id: params.instantWin.code,
            price: params.instantWin.ticket_cost,
            item_category: instantWinType,
            quantity: params.quantity,
            index: 0,
          },
        ],
      },
    })
  }
}
